import { useClerk, useSession, useOrganization } from '@clerk/react';
import { useEffect, useMemo } from 'react';
import { type AuthProvider, type UserIdentity } from 'ra-core';
import { addProviderLifecycleHooks } from '../api/addLifecycleHooks';
import { createAdminDataProvider } from '../api/adminDataProvider';
import { useFetchCurrentUser } from '../hooks/useGetCurrentUser';
import { useHttpClient } from '../api/useHttpClient';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import * as amplitude from '@amplitude/unified';

export const useReactAdminProviders = () => {
  const { signOut, openSignIn, user: clerkUser } = useClerk();
  const { session } = useSession();
  const { organization } = useOrganization();
  const client = useHttpClient();

  const dataProvider = useMemo(() => {
    const provider = createAdminDataProvider(client);
    return addProviderLifecycleHooks(provider);
  }, [client]);

  const {
    data: user,
    isPending,
    isError,
  } = useQuery(useFetchCurrentUser(dataProvider));

  const authProvider: AuthProvider = useMemo(
    () => ({
      checkError: () => Promise.resolve(),
      checkAuth: () => {
        if (session) {
          return Promise.resolve();
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
          return Promise.reject({ message: 'Not authenticated' });
        }
      },
      login: () => {
        openSignIn();
        return Promise.resolve();
      },
      logout: () => signOut(),
      getIdentity: async (): Promise<UserIdentity> => {
        if (!user) {
          return Promise.reject(new Error('Not signed in'));
        }

        let displayName = user.displayName;
        if (organization) {
          displayName += ` - ${organization.name}`;
        }

        return {
          id: user.id,
          fullName: displayName,
          avatar: user.pictureUrl,
        };
      },
      getPermissions: () => Promise.resolve('admin'),
    }),
    [user, openSignIn, organization, session, signOut]
  );

  useEffect(() => {
    if (!user || !clerkUser) {
      Sentry.setUser(null);
      amplitude.reset();
    } else {
      Sentry.setUser({
        id: user.id,
        email: clerkUser.primaryEmailAddress?.emailAddress,
        username:
          clerkUser.primaryPhoneNumber?.phoneNumber ||
          clerkUser.primaryEmailAddress?.emailAddress ||
          undefined,
      });

      const tenantId = organization?.publicMetadata.tenantId || null;

      Sentry.setContext('User details', { ...user });
      Sentry.setTag('tenantId', tenantId);

      amplitude.setUserId(clerkUser.id);

      if (tenantId) {
        const identifyEvent = new amplitude.Identify();
        identifyEvent.set('tenant', tenantId);
        amplitude.identify(identifyEvent);
      }
    }
  }, [user, clerkUser, organization]);

  return {
    authProvider,
    dataProvider,
    user,
    isLoading: isPending,
    isError,
  };
};
